import type { CSSProperties } from 'react';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Card, Form, Input, Tooltip } from '@arco-design/web-react';
import { IconDelete, IconPlus, IconQuestionCircle } from '@arco-design/web-react/icon';

import { safeParseJSON } from '@/utils/json-parse';

import { buildPresetListKeySet, resolveNamedListItemKey } from '../../platform/access-policy/orderListItemLock';
import { isFieldDisabled, isFieldReadonly } from '../../platform/capability-registry/fieldControl';
import { isActionVisible, isFieldVisible } from '../../platform/capability-registry/resolveFieldMode';
import { useOptionalResourceFormContext } from '../../platform/resource-form';
import { ENV_VARS_ACTION_KEYS, ENV_VARS_FIELD_KEYS } from './fieldKeys';
import styles from './index.module.less';
import type { EnvVarFormRow, EnvVarRowMeta, EnvVarsBlockProps } from './types';

const FormList = Form.List;
const FormItem = Form.Item;

function requiredRules(message: string) {
  return [{ required: true, message }];
}

function ensureEnvListField(form: EnvVarsBlockProps['form'], listPath: string) {
  const current = form.getFieldValue(listPath);
  if (current === undefined || current === null) {
    form.setFieldValue(listPath, []);
  }
}

function isCustomEnvRow(meta: EnvVarRowMeta, row?: EnvVarFormRow) {
  if (meta.custom) return true;
  if (!row?._self?.trim()) return true;
  return false;
}

/** 环境变量 Form.List 能力块 */
export function EnvVarsBlock(props: EnvVarsBlockProps) {
  const { form, paths, options, title, ...control } = props;
  const { t } = useTranslation();
  const readonly = isFieldReadonly(ENV_VARS_FIELD_KEYS.list, control);
  const disabled = isFieldDisabled(ENV_VARS_FIELD_KEYS.list, control);
  const useCard = options?.useCard === true;
  /**
   * preview / 显式 readonly：禁增删；
   * 下单 orderListItemLock 时 list 保持 editable，可增，预置行由 skuPresetKeys 禁删改。
   */
  const canAdd = isActionVisible(ENV_VARS_ACTION_KEYS.add, control) && !readonly && !disabled;
  const canRemove = isActionVisible(ENV_VARS_ACTION_KEYS.remove, control) && !readonly && !disabled;
  const resourceFormCtx = useOptionalResourceFormContext();
  const skuPresetKeys = useMemo(
    () => buildPresetListKeySet(resourceFormCtx?.presetConfigSnapshot, paths.list, (row) => resolveNamedListItemKey(row, 'name')),
    [paths.list, resourceFormCtx?.presetConfigSnapshot],
  );

  useEffect(() => {
    ensureEnvListField(form, paths.list);
  }, [form, paths.list]);

  if (!isFieldVisible(ENV_VARS_FIELD_KEYS.list, control)) {
    return null;
  }

  const addCustomEnv = (add: (item: EnvVarFormRow) => void) => {
    ensureEnvListField(form, paths.list);
    add({
      name: '',
      value: '',
      _self: JSON.stringify({ custom: true, editable: true, visible: true } satisfies EnvVarRowMeta),
    });
  };

  const removeEnvRow = (remove: (index: number) => void, index: number) => {
    const list = form.getFieldValue(paths.list);
    const beforeLen = Array.isArray(list) ? list.length : 0;
    remove(index);
    if (beforeLen <= 1) {
      options?.onListEmpty?.();
    }
  };

  const content = (
    <FormList field={paths.list}>
      {(fields, { add, remove }) => (
        <div>
          {fields.map((item, index) => {
            const curr = form.getFieldValue(paths.list)?.[index] as EnvVarFormRow | undefined;
            const currObj = safeParseJSON(curr?._self, {}) as EnvVarRowMeta & { custom?: boolean };
            const isCustom = isCustomEnvRow(currObj, curr);
            const inSkuPreset = skuPresetKeys.has(String(curr?.name ?? '').trim());
            const nameLocked = inSkuPreset || (!isCustom && !!currObj?.key);
            const valueDisabled = disabled || inSkuPreset || (!isCustom && currObj?.editable === false);
            const rowHidden = currObj?.visible === false;
            const showRemove = canRemove && !inSkuPreset && (isCustom || !currObj?.key);
            const rowReadOnly = readonly || inSkuPreset;

            return (
              <FormItem key={item.key} hidden={rowHidden} className={styles.row}>
                <div className={styles.rowInner}>
                  <FormItem
                    field={`${item.field}.name`}
                    className={styles.cell}
                    rules={rowReadOnly || nameLocked ? undefined : requiredRules(t('common:key'))}
                    disabled={disabled || nameLocked}
                    noStyle
                  >
                    <Input readOnly={rowReadOnly} placeholder={t('common:key')} />
                  </FormItem>
                  <FormItem
                    field={`${item.field}.value`}
                    className={styles.cell}
                    rules={rowReadOnly || !currObj?.required ? undefined : requiredRules(t('common:value'))}
                    disabled={valueDisabled}
                    noStyle
                  >
                    <Input readOnly={rowReadOnly} placeholder={t('common:value')} />
                  </FormItem>
                  <FormItem field={`${item.field}._self`} hidden>
                    <Input />
                  </FormItem>
                  {currObj?.description ? (
                    <Tooltip content={currObj.description}>
                      <IconQuestionCircle className={styles.hintIcon} />
                    </Tooltip>
                  ) : showRemove ? (
                    <Button icon={<IconDelete />} type="text" onClick={() => removeEnvRow(remove, index)} />
                  ) : (
                    <IconQuestionCircle className={styles.hintIconPlaceholder} />
                  )}
                </div>
              </FormItem>
            );
          })}
          {canAdd ? (
            <Button type="primary" size="mini" icon={<IconPlus />} onClick={() => addCustomEnv(add)}>
              {t('common:add')}
            </Button>
          ) : null}
        </div>
      )}
    </FormList>
  );

  if (useCard) {
    const cardStyle: CSSProperties = { borderStyle: 'dashed', borderRadius: 8, ...(options?.style ?? {}) };
    return (
      <div>
        {title ? <div className={styles.cardTitle}>{title}</div> : null}
        <Card style={cardStyle}>{content}</Card>
      </div>
    );
  }

  return (
    <div>
      {title ? <div className={styles.title}>{title}</div> : null}
      {content}
    </div>
  );
}
